import type { FC } from "react"

import { InformationCircleIcon } from "@heroicons/react/24/outline"

import type {
  ConsumptionInsightsReason,
  ConsumptionInsightsUnavailableEvidenceDto,
  TypicalDayViewDto,
} from "~/api/dto/proposal-consumption.dto"

import { formatKw } from "./chart-theme"
import { LoadShapeChart } from "./LoadShapeChart"

export const TYPICAL_DAY_EMPTY_COPY = `Hour-by-hour views need captured Green Button interval data. Connect the utility account and wait for its readings to arrive.`

export const TYPICAL_DAY_GRID_IMPORT_COPY = `This profile reflects grid import rather than native household load, so Typical day is not available. Other insights may still be available when their evidence qualifies.`

export const TYPICAL_DAY_INSUFFICIENT_DAYS_COPY = `Green Button data is connected, but fewer than seven complete local days are available. Typical day needs more observed days before it can be shown.`

export const insufficientEvidenceCopy = (
  viewName: string,
  evidence: ConsumptionInsightsUnavailableEvidenceDto | undefined,
  fallback: string,
): string => {
  const observedDayCount = Math.max(
    evidence?.importObservedDayCount ?? 0,
    evidence?.netObservedDayCount ?? 0,
  )
  if (observedDayCount < 7) return fallback

  return `Green Button contains ${observedDayCount.toLocaleString()} complete local days, but they do not satisfy the safe ${viewName} evidence policy.`
}

const EVENING_LOAD_SHARE_DEFINITION = `Evening Load Share is the share of one day's consumed energy that falls within the fixed 4pm–9pm window.`

const formatCoverageDate = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
    timeZone: `UTC`,
  }).format(date)
}

export const formatTypicalDayBasisCopy = (
  viewModel: TypicalDayViewDto,
): string => {
  const channelLabel =
    viewModel.measurementBasis === `GRID_IMPORT`
      ? `grid import`
      : viewModel.channelBasis === `netKwh`
        ? `net usage`
        : `import consumption`
  const statusLabel =
    viewModel.evidenceStatus === `complete` ? `Complete` : `Partial`
  return `${statusLabel} ${channelLabel} · ${viewModel.observedDayCount} observed days · ${viewModel.coveragePercent}% of a year · ${formatCoverageDate(viewModel.coverageStart)} – ${formatCoverageDate(viewModel.coverageEnd)}`
}

type InsightTileProps = {
  hint: string
  label: string
  value: string
  tooltip?: string
}

const InsightTile: FC<InsightTileProps> = ({ hint, label, value, tooltip }) => (
  <div className="rounded-xl border border-border bg-background px-5 py-4">
    <div className="flex items-center gap-1.5">
      <p className="font-display text-xs font-semibold tracking-wide text-muted uppercase">
        {label}
      </p>
      {tooltip ? (
        <span className="inline-flex text-muted" title={tooltip}>
          <InformationCircleIcon aria-hidden="true" className="size-4" />
          <span className="sr-only">{tooltip}</span>
        </span>
      ) : null}
    </div>
    <p className="mt-2 font-display text-3xl font-bold tabular-nums text-foreground">
      {value}
    </p>
    <p className="mt-1 text-sm text-muted">{hint}</p>
  </div>
)

export const TypicalDayEmptyState: FC<{
  reason?: ConsumptionInsightsReason
  evidence?: ConsumptionInsightsUnavailableEvidenceDto
}> = ({ evidence, reason }) => {
  const copy =
    reason === `grid_import_basis`
      ? TYPICAL_DAY_GRID_IMPORT_COPY
      : reason === `insufficient_observed_days`
        ? insufficientEvidenceCopy(
            `Typical day`,
            evidence,
            TYPICAL_DAY_INSUFFICIENT_DAYS_COPY,
          )
        : TYPICAL_DAY_EMPTY_COPY

  return (
    <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
      <p className="mx-auto max-w-lg font-display text-base font-semibold text-foreground">
        {copy}
      </p>
    </div>
  )
}

export const TypicalDayTab: FC<{ viewModel: TypicalDayViewDto }> = ({
  viewModel,
}) => {
  const isPartial = viewModel.evidenceStatus === `partial`
  const isGridImport = viewModel.measurementBasis === `GRID_IMPORT`
  const peakLabel = isGridImport ? `grid import` : `demand`
  const peakHint = isPartial
    ? `Highest hourly ${peakLabel} in the observed period — ${viewModel.peakOccursAtLabel}`
    : `Highest hourly ${peakLabel} — ${viewModel.peakOccursAtLabel}`
  const eveningHint = isPartial
    ? `Share of observed-period ${isGridImport ? `grid import` : `usage`} in the fixed 4pm–9pm window`
    : `Share of daily ${isGridImport ? `grid import` : `usage`} in the 4pm–9pm window`

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted">
        {formatTypicalDayBasisCopy(viewModel)}
      </p>
      {isGridImport ? (
        <p className="text-sm text-muted">
          Electricity drawn from the grid. Solar energy used directly in the home
          is not included.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <InsightTile
          label={`Peak hourly ${peakLabel}`}
          value={formatKw(viewModel.peakHourlyKw)}
          hint={peakHint}
        />
        <InsightTile
          label={
            isGridImport ? `Evening grid import share` : `Evening Load Share`
          }
          value={`${Math.round(viewModel.eveningLoadShare * 100)}%`}
          hint={eveningHint}
          tooltip={
            isGridImport
              ? `The share of daily grid import in the fixed 4pm–9pm window.`
              : EVENING_LOAD_SHARE_DEFINITION
          }
        />
      </div>
      <div>
        <h3 className="font-display text-sm font-semibold text-foreground">
          {isGridImport ? `Typical day grid import` : `Typical day load shape`}
        </h3>
        <p className="mt-1 text-sm text-muted">
          Average {isGridImport ? `grid import` : `household load`} by hour of
          day, by season. Evening window shaded.
        </p>
        <div className="mt-4">
          <LoadShapeChart viewModel={viewModel} />
        </div>
      </div>
    </div>
  )
}
